import React from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import { useAdminData } from '../hooks/useAdminData';
import styles from './GeneratedTranscriptsPage.module.css';

const GeneratedTranscriptsPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const { transcripts, refreshAdminData } = useAdminData();

  const handleDelete = async (id?: number) => {
    if (!id) return;
    const ok = window.confirm('Delete this transcript? This action cannot be undone.');
    if (!ok) return;
    const token = window.localStorage.getItem('policyora_token');
    if (!token) {
      window.alert('Not authenticated');
      return;
    }
    try {
      // call API to delete
      const adminApi = await import('../../../api/adminApi');
      await adminApi.deleteAdminYoutubeTranscript(token, id);
      // refresh local data
      refreshAdminData();
    } catch (err) {
      console.error('Failed to delete transcript', err);
      // as fallback, remove locally
      window.alert('Failed to delete on server; removing locally');
      // remove from DOM by refreshing
      refreshAdminData();
    }
  };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel={'Generated transcripts'}
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Generated transcripts</h2>
            <p className={styles.subtitle}>All YouTube transcripts generated in this workspace</p>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => handleNavClick('transcripts')}
            >
              ← Back to transcripts
            </button>
          </div>
        </div>

        {(!transcripts || transcripts.length === 0) ? (
          <div className={styles.empty}>No transcripts found</div>
        ) : (
          <div className={styles.list}>
            {transcripts.map((t) => (
              <div key={t.id} className={styles.item}>
                <div className={styles.itemLeft}>
                  <div className={styles.itemInfo}>
                    <div className={styles.itemTitle} title={t.title || 'Untitled transcript'}>
                      {t.title || 'Untitled transcript'}
                    </div>
                    <div className={styles.itemMeta} title={t.youtube_url}>
                      {t.youtube_url}
                    </div>
                  </div>
                </div>
                <div className={styles.itemRight}>
                  <div className={styles.itemDate}>{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</div>
                  <div className={styles.itemActions}>
                    {t.download_url && (
                      <a href={t.download_url} target="_blank" rel="noopener noreferrer" className={styles.linkButton}>Download</a>
                    )}
                    {t.download_url ? (
                      <a href={`/admin/dashboard/generated-transcripts/view?url=${encodeURIComponent(t.download_url)}`} target="_blank" rel="noopener noreferrer" className={styles.ghostLink}>Open</a>
                    ) : (
                      <a href={t.youtube_url} target="_blank" rel="noopener noreferrer" className={styles.ghostLink}>Open</a>
                    )}
                    <button type="button" className={styles.deleteButton} onClick={() => handleDelete(t.id)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default GeneratedTranscriptsPage;
